import React, { useState, useEffect } from 'react';
import designApi from '@services/designApi.jsx';
import ResourceUploadField from '@components/common/ResourceUploadField.jsx';

const PostCaptureLoadingSetting = ({ themeId, themePageMaster }) => {
  // 상태 관리
  const [postCaptureLoadingPage, setPostCaptureLoadingPage] = useState({
    themePageMaster: themePageMaster,
    languageCode: 'ko',
    messageText: '사진을 처리하고 있습니다...',
    messageColor: '#FFFFFF',
    messagePositionX: 500,
    messagePositionY: 400,
    loadingSpinnerType: 'default',
    loadingSpinnerColor: '#FFFFFF',
    loadingSpinnerSize: 'medium',
    progressBarType: 'linear',
    progressBarColor: '#007BFF',
    progressBarBackgroundColor: '#E0E0E0',
    estimatedDuration: 8,
    timeoutRedirectUrl: 'photo_selection',
    logoPositionX: 500,
    logoPositionY: 200,
    completionMessage: '처리가 완료되었습니다!',
    completionMessageColor: '#00FF00'
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // API 호출로 데이터 가져오기
  useEffect(() => {
    const fetchPostCaptureLoadingPage = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await designApi.getPostCaptureLoadingPage(themeId);
        
        setPostCaptureLoadingPage({
          ...postCaptureLoadingPage,
          ...data
        });

        console.log("API 호출 성공:", data);
      } catch (err) {
        console.error("API 호출 실패:", err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPostCaptureLoadingPage();
  }, [themeId]);

  // 입력값 변경 핸들러
  const handleInputChange = (field, value) => {
    setPostCaptureLoadingPage(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 리소스 업로드 처리 함수
  const handleResourceUploaded = (resourceType, resourceId) => {
    setPostCaptureLoadingPage({
      ...postCaptureLoadingPage,
      [resourceType]: resourceId
    });
  };

  // 위치 중앙 정렬 핸들러
  const handleCenterAlign = (type) => {
    const centerX = 500;
    let centerY = 200;

    switch (type) {
      case "message":
        centerY = 400;
        setPostCaptureLoadingPage({ 
          ...postCaptureLoadingPage,
          messagePositionX: centerX, 
          messagePositionY: centerY 
        });
        break;
      case "logo":
        centerY = 200;
        setPostCaptureLoadingPage({ 
          ...postCaptureLoadingPage,
          logoPositionX: centerX, 
          logoPositionY: centerY 
        });
        break;
      default:
        break;
    }
  };

  // 예상 소요 시간 조절 핸들러
  const handleDurationChange = (value) => {
    const duration = Math.max(1, Math.min(60, parseInt(value) || 8));
    setPostCaptureLoadingPage({
      ...postCaptureLoadingPage,
      estimatedDuration: duration
    });
  };



  // 저장 이벤트 리스너
  useEffect(() => {
    const eventName = `savePageDesign_shooting_loading`;

    const saveHandler = async (event) => {
      try {
        setIsLoading(true);
        const dataToSave = {
          ...postCaptureLoadingPage,
          themeId: event.detail.themeId
        };
        const isSucceed = await designApi.savePostCaptureLoadingPage(event.detail.themeId, dataToSave);

        if(isSucceed) {
          alert("저장되었습니다.");
        } else {
          alert("저장에 실패했습니다.");
        }
      } catch (error) {
        console.error("저장 실패:", error);
        alert("저장 중 오류가 발생했습니다.");
      } finally {
        setIsLoading(false);
      }
    };

    document.addEventListener(eventName, saveHandler);
    return () => {
      document.removeEventListener(eventName, saveHandler);
    };
  }, [postCaptureLoadingPage, themeId]);

  return (
    <div className="design-settings">
      {/* 언어 선택 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">언어 선택</h4>
        <div className="option-select-container">
          <select
            className="option-select"
            value={postCaptureLoadingPage.languageCode || "ko"}
            onChange={(e) => handleInputChange('languageCode', e.target.value)}
          >
            <option value="ko">한국어</option>
            <option value="en">영어</option>
            <option value="jp">일본어</option>
            <option value="cn">중국어</option>
          </select>
          <i className="ri-arrow-down-s-line select-arrow"></i>
        </div>
      </div>

      {/* 배경 이미지 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">배경 이미지</h4>
        <ResourceUploadField
          label=""
          resourceId={postCaptureLoadingPage.backgroundResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("backgroundResourceId", id)}
          acceptTypes="image/*"
        />
      </div>

      {/* 배경 음악 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">배경 음악</h4>
        <ResourceUploadField
          label=""
          resourceId={postCaptureLoadingPage.backgroundMusicResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("backgroundMusicResourceId", id)}
          acceptTypes="audio/*"
        />
      </div>

      {/* 로딩 애니메이션 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">로딩 애니메이션</h4>
        <div className="settings-row">
          <div className="settings-label">커스텀 로딩 동영상</div>
          <ResourceUploadField
            label=""
            resourceId={postCaptureLoadingPage.loadingVideoResourceId}
            onResourceUploaded={(id) => handleResourceUploaded("loadingVideoResourceId", id)}
            acceptTypes="video/*"
          />
        </div>
        <div className="settings-row">
          <div className="settings-label">스피너 타입</div>
          <div className="option-select-container">
            <select
              className="option-select"
              value={postCaptureLoadingPage.loadingSpinnerType || "default"}
              onChange={(e) => handleInputChange('loadingSpinnerType', e.target.value)}
            >
              <option value="default">기본 스피너</option>
              <option value="dots">점 애니메이션</option>
              <option value="pulse">펄스 효과</option>
              <option value="circle">원형 스피너</option>
              <option value="custom">커스텀 동영상</option>
            </select>
            <i className="ri-arrow-down-s-line select-arrow"></i>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">스피너 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={postCaptureLoadingPage.loadingSpinnerColor || "#FFFFFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={postCaptureLoadingPage.loadingSpinnerColor || "#FFFFFF"}
              onChange={(e) => handleInputChange('loadingSpinnerColor', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">스피너 크기</div>
          <div className="option-select-container">
            <select
              className="option-select"
              value={postCaptureLoadingPage.loadingSpinnerSize || "medium"}
              onChange={(e) => handleInputChange('loadingSpinnerSize', e.target.value)}
            >
              <option value="small">작게</option>
              <option value="medium">보통</option>
              <option value="large">크게</option>
            </select>
            <i className="ri-arrow-down-s-line select-arrow"></i>
          </div>
        </div>
      </div>

      {/* 진행률 표시 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">진행률 표시</h4>
        <div className="settings-row">
          <div className="settings-label">진행바 타입</div>
          <div className="option-select-container">
            <select
              className="option-select"
              value={postCaptureLoadingPage.progressBarType || "linear"}
              onChange={(e) => handleInputChange('progressBarType', e.target.value)}
            >
              <option value="none">사용 안함</option>
              <option value="linear">선형 진행바</option>
              <option value="circular">원형 진행바</option>
              <option value="percentage">퍼센트 표시</option>
              <option value="steps">단계별 표시</option>
            </select>
            <i className="ri-arrow-down-s-line select-arrow"></i>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">진행바 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={postCaptureLoadingPage.progressBarColor || "#007BFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={postCaptureLoadingPage.progressBarColor || "#007BFF"}
              onChange={(e) => handleInputChange('progressBarColor', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">진행바 배경 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={postCaptureLoadingPage.progressBarBackgroundColor || "#E0E0E0"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={postCaptureLoadingPage.progressBarBackgroundColor || "#E0E0E0"}
              onChange={(e) => handleInputChange('progressBarBackgroundColor', e.target.value)}
            />
          </div>
        </div>
      </div>



      {/* 메인 로딩 메시지 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">메인 로딩 메시지</h4>
        <div className="settings-row">
          <div className="settings-input">
            <input 
              type="text" 
              placeholder="사진을 처리하고 있습니다..." 
              className="text-input"
              value={postCaptureLoadingPage.messageText || ""}
              onChange={(e) => handleInputChange('messageText', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={postCaptureLoadingPage.messageColor || "#FFFFFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={postCaptureLoadingPage.messageColor || "#FFFFFF"}
              onChange={(e) => handleInputChange('messageColor', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">위치</div>
          <div className="position-component">
            <div className="position-input-group">
              <span className="position-label">X</span>
              <input
                type="text"
                className="position-input"
                value={postCaptureLoadingPage.messagePositionX || 0}
                onChange={(e) =>
                  setPostCaptureLoadingPage({
                    ...postCaptureLoadingPage,
                    messagePositionX: parseInt(e.target.value) || 0,
                  })
                }
              />
              <span className="position-label">Y</span>
              <input
                type="text"
                className="position-input"
                value={postCaptureLoadingPage.messagePositionY || 0}
                onChange={(e) =>
                  setPostCaptureLoadingPage({
                    ...postCaptureLoadingPage,
                    messagePositionY: parseInt(e.target.value) || 0,
                  })
                }
              />
            </div>
            <div className="direction-buttons">
              <div className="direction-row">
                <button
                  className="direction-btn"
                  onClick={() =>
                    setPostCaptureLoadingPage({
                      ...postCaptureLoadingPage,
                      messagePositionY: (postCaptureLoadingPage.messagePositionY || 0) - 1,
                    })
                  }
                >
                  <i className="ri-arrow-up-s-fill"></i>
                </button>
              </div>
              <div className="direction-row">
                <button
                  className="direction-btn"
                  onClick={() =>
                    setPostCaptureLoadingPage({
                      ...postCaptureLoadingPage,
                      messagePositionX: (postCaptureLoadingPage.messagePositionX || 0) - 1,
                    })
                  }
                >
                  <i className="ri-arrow-left-s-fill"></i>
                </button>
                <button
                  className="direction-btn"
                  onClick={() =>
                    setPostCaptureLoadingPage({
                      ...postCaptureLoadingPage,
                      messagePositionX: (postCaptureLoadingPage.messagePositionX || 0) + 1,
                    })
                  }
                >
                  <i className="ri-arrow-right-s-fill"></i>
                </button>
              </div>
              <div className="direction-row">
                <button
                  className="direction-btn"
                  onClick={() =>
                    setPostCaptureLoadingPage({
                      ...postCaptureLoadingPage,
                      messagePositionY: (postCaptureLoadingPage.messagePositionY || 0) + 1,
                    })
                  }
                >
                  <i className="ri-arrow-down-s-fill"></i>
                </button>
              </div>
            </div>
            <button
              className="btn center-btn"
              onClick={() => handleCenterAlign("message")}
            >
              중앙정렬
            </button>
          </div>
        </div>
      </div>

      {/* 완료 메시지 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">완료 메시지</h4>
        <div className="settings-row">
          <div className="settings-input">
            <input 
              type="text" 
              placeholder="처리가 완료되었습니다!" 
              className="text-input"
              value={postCaptureLoadingPage.completionMessage || ""}
              onChange={(e) => handleInputChange('completionMessage', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={postCaptureLoadingPage.completionMessageColor || "#00FF00"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={postCaptureLoadingPage.completionMessageColor || "#00FF00"}
              onChange={(e) => handleInputChange('completionMessageColor', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* 로고 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">로고</h4>
        <ResourceUploadField
          label=""
          resourceId={postCaptureLoadingPage.logoResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("logoResourceId", id)}
          acceptTypes="image/*"
        />
        <div className="settings-row">
          <div className="settings-label">위치</div>
          <div className="position-component">
            <div className="position-input-group">
              <span className="position-label">X</span>
              <input
                type="text"
                className="position-input"
                value={postCaptureLoadingPage.logoPositionX || 0}
                onChange={(e) =>
                  setPostCaptureLoadingPage({
                    ...postCaptureLoadingPage,
                    logoPositionX: parseInt(e.target.value) || 0,
                  })
                }
              />
              <span className="position-label">Y</span>
              <input
                type="text"
                className="position-input"
                value={postCaptureLoadingPage.logoPositionY || 0}
                onChange={(e) =>
                  setPostCaptureLoadingPage({
                    ...postCaptureLoadingPage,
                    logoPositionY: parseInt(e.target.value) || 0,
                  })
                }
              />
            </div>
            <div className="direction-buttons">
              <div className="direction-row">
                <button
                  className="direction-btn"
                  onClick={() =>
                    setPostCaptureLoadingPage({
                      ...postCaptureLoadingPage,
                      logoPositionY: (postCaptureLoadingPage.logoPositionY || 0) - 1,
                    })
                  }
                >
                  <i className="ri-arrow-up-s-fill"></i>
                </button>
              </div>
              <div className="direction-row">
                <button
                  className="direction-btn"
                  onClick={() =>
                    setPostCaptureLoadingPage({
                      ...postCaptureLoadingPage,
                      logoPositionX: (postCaptureLoadingPage.logoPositionX || 0) - 1,
                    })
                  }
                >
                  <i className="ri-arrow-left-s-fill"></i>
                </button>
                <button
                  className="direction-btn"
                  onClick={() =>
                    setPostCaptureLoadingPage({
                      ...postCaptureLoadingPage,
                      logoPositionX: (postCaptureLoadingPage.logoPositionX || 0) + 1,
                    })
                  }
                >
                  <i className="ri-arrow-right-s-fill"></i>
                </button>
              </div>
              <div className="direction-row">
                <button
                  className="direction-btn"
                  onClick={() =>
                    setPostCaptureLoadingPage({
                      ...postCaptureLoadingPage,
                      logoPositionY: (postCaptureLoadingPage.logoPositionY || 0) + 1,
                    })
                  }
                >
                  <i className="ri-arrow-down-s-fill"></i>
                </button>
              </div>
            </div>
            <button
              className="btn center-btn"
              onClick={() => handleCenterAlign("logo")}
            >
              중앙정렬
            </button>
          </div>
        </div>
      </div>

      {/* 시간 설정 섹션 */}
      {/*<div className="settings-box">*/}
      {/*  <h4 className="settings-title">시간 설정</h4>*/}
      {/*  <div className="settings-row">*/}
      {/*    <div className="settings-label">예상 처리 시간</div>*/}
      {/*    <div className="duration-control-container">*/}
      {/*      <button */}
      {/*        className="duration-btn decrease-btn"*/}
      {/*        onClick={() => handleDurationChange((postCaptureLoadingPage.estimatedDuration || 8) - 1)}*/}
      {/*      >*/}
      {/*        <i className="ri-arrow-down-s-fill"></i>*/}
      {/*      </button>*/}
      {/*      <input */}
      {/*        type="number" */}
      {/*        className="duration-input" */}
      {/*        min="1"*/}
      {/*        max="60"*/}
      {/*        value={postCaptureLoadingPage.estimatedDuration || 8}*/}
      {/*        onChange={(e) => handleDurationChange(e.target.value)}*/}
      {/*      />*/}
      {/*      <button */}
      {/*        className="duration-btn increase-btn"*/}
      {/*        onClick={() => handleDurationChange((postCaptureLoadingPage.estimatedDuration || 8) + 1)}*/}
      {/*      >*/}
      {/*        <i className="ri-arrow-up-s-fill"></i>*/}
      {/*      </button>*/}
      {/*      <span className="unit-text">초</span>*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*  <div className="settings-row">*/}
      {/*    <div className="settings-label">타임아웃 시 이동할 페이지</div>*/}
      {/*    <div className="option-select-container">*/}
      {/*      <select*/}
      {/*        className="option-select"*/}
      {/*        value={postCaptureLoadingPage.timeoutRedirectUrl || "photo_selection"}*/}
      {/*        onChange={(e) => handleInputChange('timeoutRedirectUrl', e.target.value)}*/}
      {/*      >*/}
      {/*        <option value="start">시작화면</option>*/}
      {/*        <option value="capture">촬영화면</option>*/}
      {/*        <option value="photo_selection">사진선택</option>*/}
      {/*        <option value="error">오류화면</option>*/}
      {/*      </select>*/}
      {/*      <i className="ri-arrow-down-s-line select-arrow"></i>*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*</div>*/}

      {/* 효과음 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">효과음</h4>
        <ResourceUploadField
          label="처리 시작 효과음"
          resourceId={postCaptureLoadingPage.processingStartSoundResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("processingStartSoundResourceId", id)}
          acceptTypes="audio/*"
        />
        <ResourceUploadField
          label="단계 변경 효과음"
          resourceId={postCaptureLoadingPage.stepChangeSoundResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("stepChangeSoundResourceId", id)}
          acceptTypes="audio/*"
        />
        <ResourceUploadField
          label="처리 완료 효과음"
          resourceId={postCaptureLoadingPage.processingCompleteSoundResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("processingCompleteSoundResourceId", id)}
          acceptTypes="audio/*"
        />
      </div>
    </div>
  );
};

export default PostCaptureLoadingSetting;